import React, { useEffect, useRef } from 'react';

interface LiquidGaugeProps {
  value: number; // 0-100
  size?: number;
}

const LiquidGauge: React.FC<LiquidGaugeProps> = ({ value, size = 160 }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const waveOffset = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Check if dark mode is active
    const isDarkMode = document.documentElement.classList.contains('dark');

    const radius = size / 2;
    const lineWidth = radius * 0.05;
    const textPixels = radius * 0.5;
    const fillPercent = Math.min(100, Math.max(0, value)) / 100;

    // Determine colors based on percentage thresholds
    let waveColor = '#22C55E'; // green-500 for < 50%
    let circleColor = '#22C55E';
    let textColor = '#045681';
    let waveTextColor = '#A4DBf8';

    if (value > 90) {
      waveColor = '#DC2626'; // red-600
      circleColor = '#DC2626';
    } else if (value > 75) {
      waveColor = '#F59E0B'; // amber-500
      circleColor = '#F59E0B';
    } else if (value > 50) {
      waveColor = '#3B82F6'; // blue-500
      circleColor = '#3B82F6';
    }

    // Configuration
    const config = {
      circleColor,
      waveColor,
      textColor,
      waveTextColor,
      backgroundColor: isDarkMode ? '#111827' : '#F9FAFB', // dark:bg-gray-900 : bg-gray-50
      waveHeight: 0.05,
      waveCount: 1,
      waveSpeed: 0.02,
    };

    const draw = () => {
      // Re-check dark mode on each draw
      const currentDarkMode = document.documentElement.classList.contains('dark');
      config.backgroundColor = currentDarkMode ? '#111827' : '#F9FAFB';

      ctx.clearRect(0, 0, size, size);

      // Draw outer circle
      ctx.beginPath();
      ctx.arc(radius, radius, radius - lineWidth / 2, 0, 2 * Math.PI);
      ctx.strokeStyle = config.circleColor;
      ctx.lineWidth = lineWidth;
      ctx.stroke();

      // Create clipping region for the inner circle
      ctx.save();
      ctx.beginPath();
      ctx.arc(radius, radius, radius - lineWidth, 0, 2 * Math.PI);
      ctx.clip();

      // Draw background
      ctx.fillStyle = config.backgroundColor;
      ctx.fillRect(0, 0, size, size);

      // Calculate wave position
      const waveHeight = radius * config.waveHeight;
      const waveY = radius + (radius - lineWidth) * (1 - fillPercent * 2);

      // Draw wave
      ctx.beginPath();
      ctx.moveTo(0, waveY);

      for (let x = 0; x <= size; x++) {
        const y = waveY + Math.sin((x / radius) * Math.PI * config.waveCount + waveOffset.current) * waveHeight;
        ctx.lineTo(x, y);
      }

      ctx.lineTo(size, size);
      ctx.lineTo(0, size);
      ctx.closePath();

      // Apply gradient to wave
      const gradient = ctx.createLinearGradient(0, waveY - waveHeight, 0, size);
      gradient.addColorStop(0, config.waveColor + 'E6'); // 90% opacity
      gradient.addColorStop(1, config.waveColor + 'B3'); // 70% opacity
      ctx.fillStyle = gradient;
      ctx.fill();

      // Draw wave line
      ctx.beginPath();
      ctx.moveTo(0, waveY);
      for (let x = 0; x <= size; x++) {
        const y = waveY + Math.sin((x / radius) * Math.PI * config.waveCount + waveOffset.current) * waveHeight;
        ctx.lineTo(x, y);
      }
      ctx.strokeStyle = config.waveColor + '33'; // 20% opacity
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.restore();

      // Set up text properties
      const textY = radius;

      // Draw text that's above the wave (dark blue)
      ctx.save();
      // Clip to area above the wave
      ctx.beginPath();
      for (let x = 0; x <= size; x++) {
        const y = waveY + Math.sin((x / radius) * Math.PI * config.waveCount + waveOffset.current) * waveHeight;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.lineTo(size, 0);
      ctx.lineTo(0, 0);
      ctx.closePath();
      ctx.clip();

      // Draw dark blue text
      ctx.fillStyle = config.textColor;
      ctx.font = `bold ${textPixels}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${value.toFixed(1)}%`, radius, textY - 5);
      ctx.font = `${textPixels * 0.4}px Inter, Arial, sans-serif`;
      ctx.fillText('Used', radius, textY + textPixels / 2);
      ctx.restore();

      // Draw text that's below the wave (light blue)
      ctx.save();
      // Clip to circular area
      ctx.beginPath();
      ctx.arc(radius, radius, radius - lineWidth, 0, 2 * Math.PI);
      ctx.clip();
      // Clip to area below the wave
      ctx.beginPath();
      for (let x = 0; x <= size; x++) {
        const y = waveY + Math.sin((x / radius) * Math.PI * config.waveCount + waveOffset.current) * waveHeight;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.lineTo(size, size);
      ctx.lineTo(0, size);
      ctx.closePath();
      ctx.clip();

      // Draw light blue text
      ctx.fillStyle = config.waveTextColor;
      ctx.font = `bold ${textPixels}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`${value.toFixed(1)}%`, radius, textY - 5);
      ctx.font = `${textPixels * 0.4}px Inter, Arial, sans-serif`;
      ctx.fillText('Used', radius, textY + textPixels / 2);
      ctx.restore();

      // Update wave animation
      waveOffset.current += config.waveSpeed;
      if (waveOffset.current > 2 * Math.PI) {
        waveOffset.current -= 2 * Math.PI;
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    // Watch for theme changes
    const observer = new MutationObserver(() => {
      draw();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    draw();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      observer.disconnect();
    };
  }, [value, size]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{ display: 'block' }}
    />
  );
};

export default LiquidGauge;